import { API_URL } from '../config';
import axios, { AxiosHeaders } from 'axios';

const STORAGE_KEY = 'velox_user';

export const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return config;

    const user = JSON.parse(stored);
    const headers = AxiosHeaders.from(config.headers);

    if (user?.id) {
      headers.set('x-user-id', String(user.id));
    }
    if (user?.tokenSessao) {
      headers.set('x-session-token', user.tokenSessao);
    }

    config.headers = headers;
  } catch {}

  return config;
});

export interface Session {
  id: number;
  nomeSessao: string;
  numeroWhatsapp: string | null;
  status: 'connecting' | 'qrcode' | 'connected' | 'disconnected';
  totalMessages: number;
  totalContacts: number;
  limiteDisparos: number;
  totalDisparos: number;
  statusDisparo: string;
  createdAt: string;
}

export interface Base {
  id: number;
  nomeBase: string;
  contatos: Array<{ telefone: string; cnpj?: string; [key: string]: any }>;
  totalContatos: number;
  ativo: boolean;
  createdAt: string;
  categoria: { id: number; nome: string } | null;
}

export interface Disparo {
  id: number;
  nomeDisparo: string;
  baseId: number | null;
  baseNome: string | null;
  categoriaNome: string | null;
  sessoesIds: number[];
  intervaloMin: number;
  intervaloMax: number;
  mensagem: string;
  totalContatos: number;
  totalEnviados: number;
  totalFalhas: number;
  totalRespostas: number;
  totalReenvios: number;
  totalEnviadosHistorico: number;
  totalFalhasHistorico: number;
  totalVisualizacoesHistorico: number;
  status: 'pendente' | 'agendado' | 'em_andamento' | 'limite_atingido' | 'concluido' | 'cancelado' | 'pausado';
  agendadoPara: string | null;
  retomarEm?: string | null;
  limiteDiario: number;
  createdAt: string;
}

export interface CheckNumberResult {
  phone: string;
  exists: boolean;
  validJid: string | null;
}

export const sessionsApi = {
  list: () => api.get<Session[]>('/sessions'),
  create: (nome_sessao: string, limite_disparos?: number) => api.post<Session>('/sessions', { nome_sessao, limite_disparos }),
  getQrCode: (id: number) => api.get<{ qrcode: string | null; status: string }>(`/sessions/${id}/qrcode`),
  delete: (id: number) => api.delete(`/sessions/${id}`),
  reconnect: (id: number) => api.post(`/sessions/${id}/reconnect`),
  getStats: (id: number) => api.get<{ totalMessages: number; totalContacts: number }>(`/sessions/${id}/stats`),
  disconnect: (id: number) => api.post(`/sessions/${id}/disconnect`, {}),
  update: (id: number, data: { nome_sessao?: string; limite_disparos?: number }) => api.patch<Session>(`/sessions/${id}`, data),
  getProfilePicture: (id: number) => api.get<{ photo: string | null }>(`/sessions/${id}/profile-picture`),
  updateProfilePicture: (id: number, image: string) => api.patch(`/sessions/${id}/profile-picture`, { image }),
  bulkUpdateProfilePicture: (image: string) => api.post<{ success: number; failed: number; errors: string[] }>('/sessions/bulk-profile-picture', { image }),
  getLogs: () => api.get<ConnectionLog[]>('/sessions/logs'),
  clearLogs: () => api.delete('/sessions/logs'),
  checkNumbers: (sessionId: number, phones: string[]) =>
    api.post<CheckNumberResult[]>(`/sessions/${sessionId}/check-numbers`, { phones }),
};

export interface ConnectionLog {
  id: string;
  timestamp: string;
  sessionId: number;
  sessionName: string;
  level: 'error' | 'warn' | 'info';
  message: string;
}

export const basesApi = {
  list: () => api.get<Base[]>('/bases'),
};

export interface RelatorioBase {
  baseId: number | null;
  baseNome: string;
  totalDisparos: number;
  totalLeads: number;
  totalEntregues: number;
  totalVisualizados: number;
  totalRespostas: number;
  taxaConversao: number;
  ultimoDisparo: string | null;
}

export const relatorioApi = {
  get: () => api.get<RelatorioBase[]>('/disparos/relatorio'),
};

export interface RespostaPorData {
  data: string;
  usuarioNome: string;
  totalDisparos: number;
  totalRespostas: number;
}

export const relatorioRespostasApi = {
  getPorData: () => api.get<RespostaPorData[]>('/disparos/relatorio/respostas-por-data'),
};

export interface Resposta {
  id: number;
  disparoId: number;
  phone: string;
  sessionId: number;
  textoResposta: string;
  tipoResposta: 'mensagem' | 'botao_rapido';
  receivedAt: string;
}

export const disparosApi = {
  list: () => api.get<Disparo[]>('/disparos'),
  create: (dto: {
    nomeDisparo: string;
    baseId: number | null;
    baseNome: string | null;
    categoriaNome: string | null;
    contatos: string[];
    sessoesIds: number[];
    intervaloMin: number;
    intervaloMax: number;
    mensagem: string;
    imagemUrl?: string | null;
    botoes?: Botao[];
    agendadoPara: string | null;
    cnpjManual?: string | null;
    limiteDiario?: number;
  }) => api.post<Disparo>('/disparos', dto),
  iniciar: (id: number) => api.patch<Disparo>(`/disparos/${id}/iniciar`),
  pausar: (id: number) => api.patch<Disparo>(`/disparos/${id}/pausar`),
  retomar: (id: number) => api.patch<Disparo>(`/disparos/${id}/retomar`),
  cancelar: (id: number) => api.patch(`/disparos/${id}/cancelar`),
  reenviar: (id: number) => api.patch<Disparo>(`/disparos/${id}/reenviar`),
  getStats: (id: number) => api.get<{ read: number; delivered: number; naoEntregues: number; readHistorico: number; enviadosHistorico: number; falhasHistorico: number; totalRespostas: number }>(`/disparos/${id}/stats`),
  getRespostas: (id: number) => api.get<Resposta[]>(`/disparos/${id}/respostas`),
  getNaoEntregues: (id: number) => api.get<{ phone: string; sentAt: string; status: string }[]>(`/disparos/${id}/nao-entregues`),
  getVisualizaram: (id: number, offset = 0, limit = 15) => api.get<{ items: { phone: string; readAt: string }[]; total: number }>(`/disparos/${id}/visualizaram?offset=${offset}&limit=${limit}`),
  remove: (id: number) => api.delete(`/disparos/${id}`),
};

export interface Botao {
  texto: string;
  url?: string;
  mensagemRetorno?: string;
}

export interface Template {
  id: number;
  titulo: string;
  conteudo: string;
  imagemUrl: string | null;
  botoes: Botao[];
  createdAt: string;
}

export const templatesApi = {
  list: () => api.get<Template[]>('/templates'),
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ url: string }>('/templates/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  create: (titulo: string, conteudo: string, botoes: Botao[] = [], imagemUrl?: string) =>
    api.post<Template>('/templates', { titulo, conteudo, botoes, imagemUrl }),
  update: (id: number, titulo: string, conteudo: string, botoes: Botao[] = [], imagemUrl?: string) =>
    api.put<Template>(`/templates/${id}`, { titulo, conteudo, botoes, imagemUrl }),
  remove: (id: number) => api.delete(`/templates/${id}`),
};

export interface MessageWithContact {
  id: number;
  phone: string;
  message: string;
  timestamp: string;
  status: string;
  direction: 'inbound' | 'outbound';
  mediaUrl?: string | null;
  buttons?: Botao[];
  quotedMessage?: string | null;
}

export interface ChatConversation {
  phone: string;
  name?: string;
  photo?: string;
  lastMessage: string;
  lastMessageTime: string | null;
  messageCount: number;
  hasMessages: boolean;
  hasImage?: boolean;
  hasButtons?: boolean;
}

export interface AuthUser {
  id: number;
  nomeCompleto: string;
  tipo: string;
  telas: string[];
  maxSessoes: number;
  tokenSessao?: string;
  assinaturaExpiracao?: string | null;
  modoTeste?: boolean;
  temMaturador?: boolean;
  popupMaturadorVisto?: boolean;
}

export interface MaturacaoItem {
  sessionId: number;
  sessionNome: string;
  sessionStatus: 'connecting' | 'qrcode' | 'connected' | 'disconnected';
  sessionNumero: string | null;
  maturacao: {
    status: 'ativa' | 'pausada' | 'concluida';
    diaAtual: number;
    duracaoDias: number;
    msgsHoje: number;
    iniciadoEm: string;
    ultimoEnvioEm: string | null;
    proximoEnvioEm: string | null;
  } | null;
}

export const maturacaoApi = {
  list: () => api.get<MaturacaoItem[]>('/maturacao'),
  iniciar: (sessionId: number, duracaoDias?: number) =>
    api.post(`/maturacao/${sessionId}/iniciar`, duracaoDias ? { duracaoDias } : {}),
  iniciarLote: (sessionIds: number[], duracaoDias: number) =>
    api.post('/maturacao/iniciar-lote', { sessionIds, duracaoDias }),
  pausar: (sessionId: number) => api.post(`/maturacao/${sessionId}/pausar`),
  retomar: (sessionId: number) => api.post(`/maturacao/${sessionId}/retomar`),
  parar: (sessionId: number) => api.post(`/maturacao/${sessionId}/parar`),
  dispensarPopup: () => api.post('/maturacao/popup/dispensar'),
};

export interface Usuario {
  id: number;
  nomeCompleto: string;
  email: string;
  cpf?: string | null;
  tipo: 'operador' | 'administrador';
  telas: string[];
  maxSessoes: number;
  apenasPropriosDados: boolean;
  receberLeads: boolean;
  foto: string | null;
  ativo: boolean;
  createdAt: string;
}

export interface UsuarioPayload {
  nome_completo: string;
  email: string;
  cpf?: string | null;
  senha?: string;
  tipo: 'operador' | 'administrador';
  telas: string[];
  max_sessoes: number;
  apenas_proprios_dados: boolean;
  receber_leads: boolean;
  foto?: string | null;
}

export type GoogleLoginResponse =
  | { needsProfile: true; email: string; suggestedName: string; picture: string | null }
  | (AuthUser & { tokenSessao: string; needsProfile?: false });

export const usuariosApi = {
  registrar: (nome_completo: string, email: string, cpf: string, senha: string, turnstileToken?: string) =>
    api.post<AuthUser & { tokenSessao: string }>('/usuarios/registrar', { nome_completo, email, cpf, senha, turnstileToken }),
  googleLogin: (idToken: string, profile?: { cpf?: string; nome_completo?: string }) =>
    api.post<GoogleLoginResponse>('/usuarios/google-login', { idToken, ...(profile || {}) }),
  list: () => api.get<Usuario[]>('/usuarios'),
  getSaldoDisparosMes: () => api.get<{ limiteMensal: number; enviadosMes: number; saldoRestante: number | null }>('/usuarios/me/disparos-saldo'),
  create: (payload: UsuarioPayload & { senha: string }) => api.post<Usuario>('/usuarios', payload),
  update: (id: number, payload: UsuarioPayload) => api.put<Usuario>(`/usuarios/${id}`, payload),
  toggleAtivo: (id: number) => api.patch<Usuario>(`/usuarios/${id}/toggle`),
  remove: (id: number) => api.delete(`/usuarios/${id}`),
};

export const messagesApi = {
  dispatch: (contacts: string[], message: string, dispatchId: string) =>
    api.post('/messages/dispatch', { contacts, message, dispatchId }),
  getStatus: (dispatchId: string) =>
    api.get<{ total: number; sent: number; pending: number; failed: number }>(
      `/messages/dispatch/${dispatchId}/status`,
    ),
  getSessionConversations: (sessionId: number) =>
    api.get<ChatConversation[]>(`/messages/session/${sessionId}/conversations`),
  getConversationMessages: (sessionId: number, phone: string) =>
    api.get<MessageWithContact[]>(`/messages/session/${sessionId}/conversations/${encodeURIComponent(phone)}`),
  sendSessionMessage: (sessionId: number, payload: { phone: string; message: string; replyToMessageId?: number }) =>
    api.post<MessageWithContact>(`/messages/session/${sessionId}/send`, payload),
  sendSessionMedia: (sessionId: number, payload: { phone: string; message?: string; file: File }) => {
    const fd = new FormData();
    fd.append('phone', payload.phone);
    if (payload.message) fd.append('message', payload.message);
    fd.append('file', payload.file);
    return api.post<MessageWithContact>(`/messages/session/${sessionId}/send-media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeMessage: (id: number) => api.delete(`/messages/${id}`),
};

export interface Plano {
  id: number;
  nome: string;
  valor: number;
  maxConexoes: number;
  maxDisparos: number;
  maxSessoesAtivas: number;
  especificacoes: string[];
  telas: string[];
  destaque: boolean;
  ativo: boolean;
  ordem: number;
  temMaturador?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const planosApi = {
  list: () => api.get<Plano[]>('/pagamentos/planos'),
  listAll: () => api.get<Plano[]>('/pagamentos/planos/todos'),
  create: (dto: { nome: string; valor: number; maxConexoes: number; maxDisparos: number; maxSessoesAtivas: number; especificacoes: string[]; destaque: boolean; ordem: number; telas: string[]; temMaturador?: boolean }) =>
    api.post<Plano>('/pagamentos/planos', dto),
  update: (id: number, dto: Partial<{ nome: string; valor: number; maxConexoes: number; maxDisparos: number; maxSessoesAtivas: number; especificacoes: string[]; destaque: boolean; ativo: boolean; ordem: number; telas: string[]; temMaturador: boolean }>) =>
    api.put<Plano>(`/pagamentos/planos/${id}`, dto),
  remove: (id: number) => api.delete(`/pagamentos/planos/${id}`),
};

export interface Assinante {
  id: number;
  nomeCompleto: string;
  email: string;
  assinaturaExpiracao: string | null;
  modoTeste: boolean;
  ativo: boolean;
  diasRestantes: number | null;
}

export const pagamentosApi = {
  criarPreferencia: () =>
    api.post<{ preferenceId: string; initPoint: string; publicKey: string }>('/pagamentos/preferencia'),

  criarPix: (planoId?: number, cpf?: string) =>
    api.post<{ paymentId: string; qrCode: string; qrCodeBase64: string; status: string; externalReference: string }>('/pagamentos/pix', { planoId, cpf }),

  criarPixMaturador: (cpf?: string) =>
    api.post<{ paymentId: string; qrCode: string; qrCodeBase64: string; status: string; externalReference: string }>('/pagamentos/maturador/pix', { cpf }),

  criarCartao: (tipo: 'credit_card' | 'debit_card', planoId?: number) =>
    api.post<{ initPoint: string; publicKey: string }>('/pagamentos/cartao', { tipo, planoId }),

  getPublicKey: () =>
    api.get<{ publicKey: string }>('/pagamentos/public-key'),

  criarCartaoDireto: (body: {
    token: string;
    payment_method_id: string;
    issuer_id?: string | number;
    installments: number;
    payer?: { email?: string; identification?: { type: string; number: string } };
    planoId?: number;
    addon?: 'maturador';
  }) =>
    api.post<{ paymentId: string; status: string; statusInterno: string; mensagemAmigavel: string }>(
      '/pagamentos/cartao-direto', body,
    ),

  consultarStatus: (paymentId: string) =>
    api.get<{ status: string; statusDetail: string; statusInterno: string; mensagemAmigavel: string; ativo: boolean; expiracao: string | null; modoTeste: boolean; diasRestantes: number | null }>(`/pagamentos/status/${paymentId}`),

  verificarAssinatura: () =>
    api.get<{ ativo: boolean; expiracao: string | null; modoTeste: boolean; diasRestantes: number | null; plano: { id: number; nome: string; valor: number; maxConexoes: number; maxDisparos: number; maxSessoesAtivas: number; especificacoes: string[] } | null }>('/pagamentos/assinatura'),

  getWebhookConfig: () =>
    api.get<{ webhookUrl: string; chaveSecreta: string; valorPlano: number; nomeProduto: string; descricaoProduto: string; updatedAt: string | null }>('/pagamentos/webhook-config'),

  atualizarValorPlano: (valor: number) =>
    api.patch<{ valorPlano: number }>('/pagamentos/plano', { valor }),

  getInfoPlano: () =>
    api.get<{ valorPlano: number; nomeProduto: string; descricaoProduto: string }>('/pagamentos/plano/info'),

  atualizarProduto: (nomeProduto: string, descricaoProduto: string) =>
    api.patch('/pagamentos/produto', { nomeProduto, descricaoProduto }),

  gerarNovaChave: () =>
    api.post<{ chaveSecreta: string }>('/pagamentos/webhook-config/nova-chave'),

  getWebhookLogs: (page = 1, limit = 20) =>
    api.get<{ logs: any[]; total: number }>(`/pagamentos/webhook-logs?page=${page}&limit=${limit}`),

  listarPagamentos: () =>
    api.get<any[]>('/pagamentos/lista'),

  listarAssinantes: () =>
    api.get<Assinante[]>('/pagamentos/assinantes'),

  getConfigTeste: () =>
    api.get<{ periodoTeste: number; tipoTeste: 'dias' | 'horas' }>('/pagamentos/config-teste'),

  atualizarConfigTeste: (periodoTeste: number, tipoTeste: 'dias' | 'horas') =>
    api.patch<{ periodoTeste: number; tipoTeste: string }>('/pagamentos/config-teste', { periodoTeste, tipoTeste }),
};
